import { WEEK_OPTIONS } from '@shared/constants/date.constants';
import { endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek, subDays } from 'date-fns';
import { EDateRangeType, IDateRangeBounds } from './profile-date-filter.interface';

/**
 * The bounds ProfileDateFilterComponent shows for a predefined range, relative to `now`. Shared so a
 * page restoring a persisted preset resolves exactly the dates the picker itself shows for it.
 */
export function calculatePredefinedDateRange(
  range: EDateRangeType,
  allTimeBounds: IDateRangeBounds | null,
  now: Date = new Date(),
): IDateRangeBounds {
  const today = endOfDay(now);

  switch (range) {
    case EDateRangeType.thisWeek:
      return {
        end: endOfWeek(today, WEEK_OPTIONS),
        start: startOfWeek(today, WEEK_OPTIONS),
      };

    case EDateRangeType.thisMonth:
      return {
        end: endOfMonth(today),
        start: startOfMonth(today),
      };

    case EDateRangeType.last7Days:
      return {
        end: today,
        start: startOfDay(subDays(today, 6)),
      };

    case EDateRangeType.last30Days:
      return {
        end: today,
        start: startOfDay(subDays(today, 29)),
      };

    case EDateRangeType.allTime:
      // Unlike every other case here, `allTimeBounds` comes from an external input
      // (`campaignAnalytics`/`allTimeBounds`) that can carry an arbitrary time-of-day (e.g. a
      // campaign created at 14:32). Without normalizing, that raw time leaks into `startDate`/
      // `endDate`, which then mismatches the startOfDay/endOfDay-normalized value `emitDateRange()`
      // computes - causing it to think the range "changed" and re-set the signals on every emit.
      return {
        end: endOfDay(allTimeBounds?.end ?? endOfWeek(today, WEEK_OPTIONS)),
        start: startOfDay(allTimeBounds?.start ?? startOfWeek(today, WEEK_OPTIONS)),
      };

    default:
      return {
        end: endOfWeek(today, WEEK_OPTIONS),
        start: startOfWeek(today, WEEK_OPTIONS),
      };
  }
}
